import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Administracja (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'administration',
  title: 'Administracja',
  audience:
    'Dla Administracji i osób z prawem przyjmowania zleceń: kto zatwierdza zlecenie, zapisuje je do TMS (VIA) plikiem, pilnuje, czy plik dotarł, i rozlicza zlecenia.',
  intro:
    'Administracja sprawdza zlecenie przed zapisem do TMS, zatwierdza jego treść i jawnie zleca wytworzenie pliku dla VIA. Rozdział opisuje, jak przejść od prezlecenia do zlecenia w TMS, jak czytać stan pliku i co zrobić, gdy zlecenie trzeba poprawić po zapisie.',
  sections: [
    {
      id: 'rola-administracji',
      title: 'Do czego służy praca Administracji w aplikacji',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenie przychodzi jako prezlecenie — z wiadomości od klienta albo wpisane ręcznie. Po akceptacji prezlecenia powstaje zlecenie, które Administracja uzupełnia o dane wymagane przez importer TMS, zatwierdza i zapisuje do VIA plikiem. Do TMS nie ma innej drogi: VIA co kilka minut czyta pliki z folderu importu.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'System przygotowuje, człowiek zatwierdza. Plik dla VIA powstaje wyłącznie po Twoim jawnym zatwierdzeniu i kliknięciu — nic nie wysyła się samo.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Nieczytelne = puste pole. Czego nie ma w danych, system zostawia puste i nazywa jako brak; nie wpisuje wartości „na wszelki wypadek”, bo w TMS nikt już nie odróżni jej od ustalenia z klientem.',
        },
        {
          kind: 'terms',
          title: 'Ekrany, z których korzystasz',
          items: [
            { term: 'Prezlecenia', meaning: 'Lista i karta prezlecenia — kompletność danych, akceptacja, przejście do zlecenia.' },
            { term: 'Zlecenia z maila', meaning: 'Karta zlecenia z panelami „Dane dla importera VIA” i „Zapis do VIA” oraz rekordami zapisu.' },
            { term: 'Zlecenia VIA', meaning: 'Komunikaty odczytane z VIA — sprawdzenie, czy nasze zlecenie jest po stronie TMS.' },
            {
              term: 'Pulpit przyjęcia zleceń',
              meaning: 'Kolejki Administracji: do kontroli, zatwierdzone przed plikiem, błędy zapisu, pliki czekające na TMS.',
              when: { on: ['intakeDepartments'] },
            },
            {
              term: 'Zmiany zleceń',
              meaning: 'Zgłoszone zmiany „było → ma być” do decyzji i do wpisania w TMS.',
              when: { on: ['orderChangeRegister'] },
            },
            { term: 'Rozliczenie', meaning: 'Zlecenia zrealizowane do rozliczenia i przekazania do fakturowania.', when: { on: ['orderSettlement'] } },
            { term: 'Korekty paliwowe', meaning: 'Kartoteka korekt paliwowych i przypisań kontrahentów.', when: { on: ['fuelSurcharges'] } },
          ],
        },
      ],
    },
    {
      id: 'od-prezlecenia-do-tms',
      title: 'Od prezlecenia do zapisu w TMS',
      blocks: [
        {
          kind: 'text',
          when: { on: ['sourcePreview'] },
          text: 'Karta prezlecenia pokazuje dane i sekcję „Kompletność i akceptacja”, a mail i załączniki klienta otwiera obok przycisk „Pokaż źródło” (przy prezleceniach z dokumentów stoją obok od razu). Dopóki brakuje pól wymaganych, „Akceptuj i utwórz zlecenie” jest nieaktywne, a nad przyciskami stoi „Do akceptacji brakuje: …”.',
        },
        {
          kind: 'text',
          when: { off: ['sourcePreview'] },
          text: 'Karta prezlecenia pokazuje po lewej źródło (wiadomość albo opis ręcznego wpisu), po prawej dane i sekcję „Kompletność i akceptacja”. Dopóki brakuje pól wymaganych, „Akceptuj i utwórz zlecenie” jest nieaktywne, a nad przyciskami stoi „Do akceptacji brakuje: …”.',
        },
        { kind: 'image', image: { shot: 'pre-order-card' }, caption: 'Karta prezlecenia — kompletność, akceptacja i akcje operatora.' },
        {
          kind: 'task',
          title: 'Przyjąć prezlecenie i wyeksportować do TMS',
          intro: 'Gdy prezlecenie ma komplet danych i trzeba z niego zrobić zlecenie w TMS.',
          when: { off: ['tmsExportOneStep'] },
          steps: [
            'Otwórz prezlecenie z listy „Prezlecenia”. Jeśli czegoś brakuje, kliknij „Popraw dane”, uzupełnij pola i „Zapisz zmiany”.',
            'Kliknij „Akceptuj i utwórz zlecenie” — powstaje zlecenie z naszym numerem, a aplikacja przenosi Cię na jego kartę.',
            'W panelu „Dane dla importera VIA” kliknij „Otwórz dane”, potem „Wypełnij z danych, które mamy”. Sprawdź propozycję, uzupełnij brakujące pola (punkty, godziny, objętość) i kliknij „Zapisz”.',
            'W panelu „Zapis do VIA” kliknij „Sprawdź plik”. Przeczytaj listę braków — dopóki importer odrzuciłby plik, zatwierdzić się nie da. Rozwiń „Pokaż treść pliku”, jeśli chcesz zobaczyć, co dokładnie pójdzie do VIA.',
            'Gdy panel mówi „Plik ma komplet pól wymaganych przez importer”, kliknij „Zatwierdź”.',
            'Kliknij „Wytwórz plik” (przy zleceniu z wózkiem — „Wytwórz pliki”). Plik trafia do folderu importu VIA.',
          ],
          outcome: 'Stan zlecenia zmienia się na „Plik wytworzony”, a po położeniu w folderze — „Dostarczone”. Kto i kiedy zatwierdził, zapisuje się samo.',
        },
        {
          kind: 'task',
          title: 'Przyjąć prezlecenie i wyeksportować do TMS',
          intro: 'Gdy prezlecenie ma komplet danych i trzeba z niego zrobić zlecenie w TMS.',
          when: { on: ['tmsExportOneStep'] },
          steps: [
            'Otwórz prezlecenie (z listy albo z kolejki „Do kontroli” na pulpicie). Uzupełnij braki przez „Popraw dane” i zaakceptuj prezlecenie.',
            'Uzupełnij „Dane dla importera VIA” — „Otwórz dane”, „Wypełnij z danych, które mamy”, sprawdź i „Zapisz”.',
            'W panelu „Zapis do VIA” kliknij „Eksportuj do TMS”. Otworzy się podgląd pliku (przy wózku — obu plików) z brakami i podobnymi zleceniami. Samo otwarcie niczego nie zapisuje.',
            'Sprawdź podgląd i kliknij „Potwierdź zapis” — albo „Anuluj”, jeśli coś trzeba poprawić.',
          ],
          outcome: 'Jedna decyzja zatwierdza treść i wytwarza plik. Zapisuje się tylko to, co było w podglądzie: gdy dane zmienią się w międzyczasie, zapis odmawia i podgląd trzeba otworzyć jeszcze raz.',
        },
        {
          kind: 'image',
          image: { guide: 'tmsExportOneStep', step: 2 },
          caption: 'Podgląd pliku i „Potwierdź zapis” — zatwierdzenie i plik jedną decyzją.',
          when: { on: ['tmsExportOneStep'] },
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Zatwierdzasz konkretną treść pliku, którą widzisz w podglądzie. Akceptowanie czegoś, czego nie widać, jest akceptacją tylko z nazwy — dlatego podgląd i lista braków są zawsze dostępne przed zapisem.',
        },
        {
          kind: 'terms',
          title: 'Co widać w panelu „Zapis do VIA”',
          items: [
            { term: 'Importer odrzuci ten plik — brakuje …', meaning: 'Lista pól, bez których VIA nie utworzy zlecenia albo utworzy je błędnie. Importer nie zgłasza odrzucenia, więc to jedyny moment, żeby to naprawić.' },
            { term: 'Do sprawdzenia (n)', meaning: 'Ostrzeżenia, które nie blokują pliku, ale warto je przeczytać przed zatwierdzeniem.' },
            { term: 'Do VIA pójdą 2 pliki', meaning: 'Zlecenie wymaga wózka: obok transportu powstaje osobna czynność wózkowa. Jej cenę i liczbę czynności wpisz w „Dane dla importera VIA”. Oba pliki wychodzą jednym kliknięciem.' },
            { term: 'Dostarczanie: …', meaning: 'Dokąd trafi plik. Tryb dostarczania ustawia wdrożenie, nie operator — tu tylko go widać.' },
            { term: 'Rekomendowana stawka VAT', meaning: 'Podpowiedź z krajów spółki, kontrahenta, załadunku i rozładunku. To nie zapis — stawkę wpisujesz w TMS.' },
          ],
        },
        {
          kind: 'task',
          title: 'Sprawdzić mail, z którego jest prezlecenie',
          when: { on: ['emailDetails'] },
          intro: 'Gdy coś w prezleceniu budzi wątpliwość, otwórz mail ze „Skrzynka” albo z karty prezlecenia.',
          steps: [
            'W sekcji „Wiadomość” sprawdź „Od”, „Do”, „DW” i „Odpowiedz do” — pytanie do klienta wysyłasz na adres z „Odpowiedz do”, jeśli jest.',
            '„Wątek” pokazuje całą korespondencję sprawy; zmiana zlecenia bywa w późniejszym mailu — przejdź do niego jednym kliknięciem.',
            'W „Załączniki” plik „w załączniku” pochodzi z wnętrza innego pliku (np. z przesłanego maila) — sprawdź, którego dotyczy.',
          ],
          outcome: 'Ostrzeżenie „Nadawca nie przeszedł weryfikacji” to informacja, nie blokada: zmianę danych z takiego maila potwierdź u klienta, zanim zatwierdzisz plik do TMS.',
        },
      ],
    },
    {
      id: 'okno-kontroli',
      title: 'Okno kontroli prezlecenia',
      when: { on: ['intakeControlWindow'] },
      blocks: [
        {
          kind: 'text',
          text: 'Przycisk „Okno kontroli” na karcie prezlecenia otwiera w jednym miejscu wszystko, co sprawdzasz przed zapisem do VIA: pola blokujące plik (osobno dla zlecenia i czynności wózkowej), rekomendowaną stawkę VAT z uzasadnieniem, podobne zlecenia, podgląd plików i stan zapisu. Zatwierdzenie i plik — tymi samymi przyciskami co na zleceniu.',
        },
        { kind: 'image', image: { guide: 'intakeControlWindow', step: 2 }, caption: 'Pola blokujące zapis — dopóki lista nie jest pusta, plik nie powstanie.' },
        {
          kind: 'task',
          title: 'Sprawdzić prezlecenie w oknie kontroli',
          steps: [
            'Na karcie prezlecenia kliknij „Okno kontroli”.',
            'Przejrzyj listę pól blokujących zapis i uzupełnij braki w „Dane dla importera”.',
            'Porównaj rekomendowaną stawkę VAT z tym, co wpiszesz w TMS.',
            'Rozwiń podgląd pliku, zanim go zatwierdzisz, i dopiero wtedy zapisz plik do VIA.',
          ],
          outcome: 'Numer zlecenia nadaje TMS, więc wynik zapisu widać dopiero po imporcie z TMS.',
        },
        {
          kind: 'task',
          title: 'Porównać prezlecenie ze źródłem',
          when: { on: ['sourcePreview'], off: ['preOrderDocuments'] },
          steps: [
            'W oknie kontroli kliknij „Pokaż źródło” — po lewej otworzy się mail klienta i jego załączniki, sekcje okna przesuną się na prawo.',
            'Zakładka „Mail” pokazuje treść wiadomości; kolejne zakładki to załączniki i pliki od klienta. PDF i zdjęcie otwierają się na miejscu, arkusz i dokument pobierasz.',
            'Przy polu bez wartości kliknij „pokaż w źródle”, żeby od razu zobaczyć właściwy załącznik.',
          ],
          outcome: 'Źródło jest tylko do odczytu — braki uzupełnia CS albo poprawiasz je w danych prezlecenia, nie w źródle.',
        },
        { kind: 'image', when: { on: ['sourcePreview'] }, image: { guide: 'sourcePreview', step: 5 }, caption: '„Pokaż źródło” w oknie kontroli.' },
        {
          kind: 'task',
          title: 'Sprawdzić, czy prezlecenie jest gotowe do wysłania',
          when: { on: ['preOrderDocuments'] },
          intro: 'Zanim zlecenie powstanie i zanim zatwierdzisz plik do TMS.',
          steps: [
            'Na liście prezleceń szukaj w kolumnie „Stan” znacznika „Gotowe do wysłania” — przy pozostałych stoi „do TMS brakuje: n”.',
            'Na karcie prezlecenia karta „Gotowe do wysłania” wypisuje braki według grup; te same pola są zaznaczone przy danych i w „Dane dla importera TMS”.',
            'Braki uzupełnia CS albo Ty w „Dane dla importera TMS”; dokument, z którego je odczytasz, stoi obok danych od razu — także w oknie kontroli („Zwiń podgląd dokumentu” go chowa).',
          ],
          outcome: '„Gotowe do wysłania” liczy się tymi samymi regułami, które sprawdzają plik przed zapisem w TMS. To informacja, nie automat: zlecenie tworzysz i plik zatwierdzasz Ty.',
        },
        {
          kind: 'task',
          title: 'Podjąć decyzje z listy „Do zrobienia”',
          when: { on: ['intakeControlDecisions'] },
          intro: 'Lista „Do zrobienia (n)” zbiera decyzje, które trzeba podjąć przed eksportem.',
          steps: [
            'Przy spółce sprawdź, skąd jest wpisana, i kliknij „Potwierdź” albo „Inna…”. Inna spółka wymaga uzasadnienia (co najmniej 10 znaków) i „Zapisz decyzję”.',
            'Przy stawce VAT porównaj podpowiedź z tym, co wpiszesz w TMS, i kliknij „Potwierdź” albo „Inna…” z uzasadnieniem.',
            'Gdy lista jest pusta, przycisk „Eksportuj do TMS” staje się aktywny.',
          ],
          outcome: 'Gdy dane, na których oparto decyzję, się zmienią, decyzja wraca na listę jako nieaktualna. „Historia decyzji” pokazuje kto, kiedy i co postanowił.',
        },
        {
          kind: 'image',
          image: { guide: 'intakeControlDecisions', step: 1 },
          caption: 'Lista „Do zrobienia (n)” — decyzje przed eksportem do TMS.',
          when: { on: ['intakeControlDecisions'] },
        },
        {
          kind: 'list',
          title: 'Cena i dodatki',
          when: { on: ['intakeControlPriceSurcharges'] },
          items: [
            'Przy cenie wskaż jej źródło: „Z dokumentu klienta…” albo „SPOT…”. „Z cennika” jest nieaktywne, dopóki nie ma cenników.',
            'Stawka SPOT wymaga śladu: kto ją ustalił, kiedy i skąd (telefon, mail poza systemem, ustalenie ustne albo inne z opisem).',
            'Gdy nie wiadomo, skąd jest cena, kliknij „Zwróć do CS: potwierdź SPOT”, popraw „Pytanie do CS” i kliknij „Zwróć do CS” — podjęte decyzje zostają.',
            'Dodatki z prezlecenia (korekta paliwowa kontrahenta, czynność wózkowa) — przy każdym „Potwierdź” albo „Odrzuć…” z powodem. Wszystko to blokuje eksport jak spółka i VAT.',
          ],
        },
      ],
    },
    {
      id: 'stan-pliku',
      title: 'Stan pliku i dostarczenie do VIA',
      blocks: [
        {
          kind: 'text',
          text: 'Stan zapisu widać na karcie zlecenia (znacznik przy numerze), w panelu „Zapis do VIA” i w tabeli „Rekordy zapisu do VIA / TMS”, gdzie każdy plik ma rodzaj, nazwę, stan i miejsce dostarczenia. Pod przyciskami stoi „Ostatni zapis: … · próba n” i lista „Próby dostarczenia” z czasem, wynikiem i komunikatem błędu każdej próby.',
        },
        {
          kind: 'terms',
          title: 'Stany zapisu do VIA',
          items: [
            { term: 'Szkic', meaning: 'Zlecenie nie jest zatwierdzone. Można zmieniać dane i sprawdzać plik.' },
            { term: 'Zatwierdzone do zapisu', meaning: 'Treść zatwierdzona, plik jeszcze nie powstał.', when: { off: ['tmsExportOneStep'] } },
            { term: 'Plik wytworzony', meaning: 'Plik powstał i czeka na położenie w folderze importu.', when: { off: ['tmsExportOneStep'] } },
            { term: 'Dostarczone', meaning: 'Plik leży w folderze importu VIA. VIA czyta folder co kilka minut.', when: { off: ['tmsExportOneStep'] } },
            {
              term: 'Wysłane do TMS (plik wytworzony / dostarczone)',
              meaning: 'Plik wyszedł i czeka na import z TMS. Sekcja mówi, kiedy i kto zatwierdził.',
              when: { on: ['tmsExportOneStep'] },
            },
            { term: 'Wykryte w VIA / Zaakceptowane w VIA', meaning: 'Zlecenie jest już po stronie VIA — pliku nie da się nadpisać.' },
            {
              term: 'Jest w TMS',
              meaning: 'Zlecenie widać w imporcie z TMS — rozpoznane po numerze zamówienia klienta albo naszym numerze, albo połączone ręcznie. Kolejny plik byłby duplikatem.',
              when: { off: ['tmsExportOneStep'] },
            },
            {
              term: 'Zapisane w TMS',
              meaning: 'Import z TMS pokazał zlecenie, z numerem TMS. Kolejny plik byłby duplikatem.',
              when: { on: ['tmsExportOneStep'] },
            },
            { term: 'Błąd zapisu', meaning: 'Plik nie dotarł. Komunikat błędu stoi pod stanem — popraw przyczynę i ponów.' },
          ],
        },
        {
          kind: 'task',
          title: 'Sprawdzić, czy plik dotarł do VIA',
          steps: [
            'Otwórz zlecenie z listy „Zlecenia z maila” albo z karty prezlecenia (sekcja „Zlecenia z tego prezlecenia”, przycisk „Otwórz”).',
            'Spójrz na znacznik stanu przy numerze zlecenia i na tabelę „Rekordy zapisu do VIA / TMS”.',
            'W panelu „Zapis do VIA” kliknij „Sprawdź plik” i przeczytaj „Ostatni zapis” oraz „Próby dostarczenia”.',
            'Jeśli stan to „Błąd zapisu”, przeczytaj komunikat, usuń przyczynę i kliknij „Połóż plik ponownie”.',
            'Po imporcie z TMS sprawdź, czy zlecenie ma stan „Jest w TMS” — dopiero wtedy wiadomo, że VIA je przyjęła.',
          ],
          when: { off: ['tmsExportOneStep'] },
          outcome: 'Ponowienie kładzie ten sam zatwierdzony plik — treść się nie zmienia.',
        },
        {
          kind: 'task',
          title: 'Sprawdzić, czy plik dotarł do VIA',
          steps: [
            'Otwórz zlecenie albo prezlecenie i znajdź panel „Zapis do VIA” (w oknie kontroli — „Stan zapisu do VIA”).',
            'Kliknij „Sprawdź plik” i przeczytaj „Ostatni zapis” oraz „Próby dostarczenia”.',
            'Przy błędzie usuń przyczynę i kliknij „Ponów wysłanie do TMS”.',
            'Po imporcie z TMS sprawdź, czy stan zmienił się na „Zapisane w TMS” z numerem TMS.',
          ],
          when: { on: ['tmsExportOneStep'] },
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['viaDeliveryAutoRetry'] },
          text: 'Gdy plik nie dotrze, aplikacja sama ponawia dostarczenie tego samego, zatwierdzonego pliku — bez nowej treści — domyślnie 3 razy co 15 minut. Panel mówi, która to próba i kiedy następna. Do kolejki „Błąd zapisu do VIA” zlecenie trafia dopiero po wyczerpaniu prób albo gdy ponawianie trzeba przerwać (zmiana danych, podobne zlecenie).',
        },
        {
          kind: 'image',
          image: { guide: 'viaDeliveryAutoRetry', step: 2 },
          caption: 'Ponawianie w panelu „Zapis do VIA” — numer próby i termin następnej.',
          when: { on: ['viaDeliveryAutoRetry'] },
        },
        {
          kind: 'task',
          title: 'Rozstrzygnąć ostrzeżenie o możliwym duplikacie',
          when: { on: ['viaWriteDuplicateCheck'] },
          intro: 'Po sprawdzeniu pliku panel pokazuje zlecenia, które mogą być tym samym transportem (ta sama spółka, dzień załadunku i rozładunku, kraje i miasta).',
          steps: [
            'Otwórz każde podobne zlecenie z odnośnika i porównaj z tym, które zapisujesz.',
            'Jeśli to duplikat zlecenia z TMS — nie wytwarzaj pliku, kliknij „To duplikat — połącz na karcie zlecenia z TMS”.',
            'Jeśli to inny transport, wpisz powód w „Dlaczego to nie duplikat” i kliknij „To nie duplikat — zapisz powód”.',
          ],
          outcome: 'Dopiero potem można wytworzyć albo ponownie położyć plik. Wpis trafia do historii obu zleceń.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Kopia zlecenia w VIA powstaje, gdy ten sam transport zapiszesz drugi raz. Zanim położysz plik ponownie, upewnij się, że poprzedni nie został już przyjęty w VIA.',
        },
      ],
    },
    {
      id: 'zmiana-po-zatwierdzeniu',
      title: 'Poprawki po zatwierdzeniu i po zapisie do VIA',
      blocks: [
        {
          kind: 'text',
          text: 'Zatwierdzasz konkretną treść. Dlatego każda zmiana danych po zatwierdzeniu cofa zlecenie do szkicu i czyści zatwierdzenie — także gdy plik już powstał albo był błąd. Aplikacja mówi wtedy: „Zlecenie wróciło do szkicu — dane zmieniły się po zatwierdzeniu, więc akceptacja wymaga powtórzenia”. Zapis bez żadnej zmiany niczego nie cofa.',
        },
        {
          kind: 'terms',
          title: 'Co wolno w którym momencie',
          items: [
            { term: 'Przed przyjęciem w VIA', meaning: 'Plik można nadpisać: popraw dane, zatwierdź ponownie i wytwórz nowy plik — dopiero nowy, zatwierdzony plik zastępuje ten w folderze.' },
            { term: 'Po przyjęciu w VIA', meaning: 'Nadpisanie zrobiłoby drugie zlecenie. Zmiana = usuń zlecenie w VIA i wprowadź je ponownie.' },
          ],
        },
        {
          kind: 'task',
          title: 'Poprawić zlecenie po zapisie do VIA',
          intro: 'Gdy zlecenie jest już w VIA albo w TMS, a dane trzeba zmienić.',
          steps: [
            'Usuń zlecenie w VIA (po stronie TMS — aplikacja niczego w VIA nie usuwa).',
            'Na karcie zlecenia w panelu „Zapis do VIA” wpisz „Powód wycofania” — co się stało i kiedy usunięto zlecenie w VIA (co najmniej 10 znaków).',
            'Kliknij „Usunąłem w VIA — wróć do szkicu”.',
            'Popraw dane w „Dane dla importera VIA” i kliknij „Zapisz”.',
            'Sprawdź plik, zatwierdź i wytwórz go ponownie — tak jak przy pierwszym zapisie.',
          ],
          outcome: 'Powód wycofania trafia do dziennika — po miesiącu nikt tego nie odtworzy z pamięci.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Poprawki po akceptacji prezlecenia robi się na zleceniu, które z niego powstało, nie na prezleceniu — inaczej nie wiadomo, co właściwie zostało przyjęte.',
        },
      ],
    },
    {
      id: 'zlecenia-via',
      title: 'Zlecenia VIA',
      menu: ['via-orders'],
      blocks: [
        {
          kind: 'text',
          text: 'Lista komunikatów odczytanych z VIA: numer zlecenia, typ komunikatu, spółka, klient, załadunek, rozładunek, data, cena i waluta. Służy do sprawdzenia, czy zlecenie jest po stronie TMS i w jakim kształcie. Filtry: „Szukaj”, „Spółka”, „Typ komunikatu”.',
        },
        { kind: 'image', image: { shot: 'via-orders' }, caption: 'Zlecenia VIA — lista komunikatów odczytanych z TMS (w danych pokazowych jeszcze pusta).' },
        {
          kind: 'terms',
          title: 'Typy komunikatów',
          items: [
            { term: 'Przydzielenie zlecenia', meaning: 'Zlecenie przydzielone przewoźnikowi.' },
            { term: 'Przyjęcie zlecenia', meaning: 'Zlecenie przyjęte.' },
            { term: 'Zmiana zlecenia', meaning: 'Pełny obraz zlecenia po zmianie — dopasowanie po numerze transportu, nie po kolejności.' },
            { term: 'Nierozpoznany', meaning: 'Komunikat, którego nie udało się odczytać — pełne dane są w zwiniętym „Surowe poddrzewo komunikatu” na karcie.' },
          ],
        },
        {
          kind: 'task',
          title: 'Wczytać komunikat z VIA i sprawdzić powiązanie',
          steps: [
            'Otwórz „Zlecenia VIA” w menu.',
            'Kliknij „Wczytaj komunikat XML” i wskaż plik otrzymany od klienta.',
            'Przeczytaj wynik: „Powiązano z naszym zleceniem …” ze stanem zapisu albo „nie rozpoznano go jako nasz” — to normalne dla zleceń, których nie wysyłaliśmy.',
            'Kliknij wiersz, żeby zobaczyć szczegóły komunikatu.',
          ],
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Na karcie zlecenia sekcja „Zlecenie w VIA” pokazuje dopasowany komunikat i przycisk „Otwórz w VIA”. Dopóki nie ma dopasowania, stoi tam informacja, że po dostarczeniu pliku i ponownym odczycie zlecenie zostanie oznaczone.',
        },
      ],
    },
    {
      id: 'polaczenie-z-tms',
      title: 'Połączenie zlecenia z TMS',
      blocks: [
        {
          kind: 'text',
          text: 'Import z TMS sam łączy zlecenie z naszym prezleceniem, gdy znajdzie nasz numer albo numer zamówienia klienta. Wtedy zlecenie dostaje stan „Jest w TMS”, a kolejnego pliku już się nie wytwarza. Gdy TMS numeru nie oddał, ale spółka, dni i miejsca się zgadzają, karta zlecenia z TMS mówi „To zlecenie z TMS może pochodzić z prezlecenia”.',
        },
        {
          kind: 'task',
          title: 'Połączyć ręcznie zlecenie z TMS z prezleceniem',
          when: { on: ['preOrderTmsManualLink'] },
          steps: [
            'Otwórz kartę zlecenia z TMS z komunikatem „To zlecenie z TMS może pochodzić z prezlecenia”.',
            'Wybierz z listy pasujące prezlecenie albo wpisz nasz numer zlecenia w polu „Inne prezlecenie — nasz numer”.',
            'Wpisz „Powód połączenia” i kliknij „Połącz z …”.',
          ],
          outcome: 'Wiersz z prezlecenia przejmuje numer, dane i punkty trasy z TMS i zachowuje historię i warianty; wiersz z TMS znika z wpisem, dokąd poszły dane. Gdy na wierszu z TMS ktoś już pracował, połączenie jest odrzucane.',
        },
        {
          kind: 'task',
          title: 'Sprawdzić połączenie mimo innego kontrahenta',
          when: { on: ['preOrderLinkOtherContractor'] },
          intro: 'Import łączy prezlecenie z TMS po numerze zamówienia klienta także wtedy, gdy kontrahent się różni — obowiązuje kontrahent z TMS.',
          steps: [
            'Otwórz kartę zlecenia ze znacznikiem „Połączono z TMS mimo innego kontrahenta — sprawdź”.',
            'Porównaj dane z prezleceniem i historią zlecenia — czy to ten sam transport i właściwy kontrahent.',
            'Wpisz komentarz (nieobowiązkowy) i kliknij „Sprawdziłem”.',
          ],
          outcome: 'Znacznik znika, potwierdzenie zostaje w historii zlecenia.',
        },
      ],
    },
    {
      id: 'zmiany-zlecen',
      title: 'Zmiany zleceń',
      menu: ['order-changes'],
      when: { on: ['orderChangeRegister'] },
      blocks: [
        {
          kind: 'text',
          text: 'Zmiana zlecenia po jego przyjęciu (okno załadunku lub rozładunku, miejsce, waga, LDM, stawka, uwagi) jest zgłaszana na karcie zlecenia w sekcji „Zmiany zlecenia (było → ma być)”. „Było” bierze się z danych zlecenia, „ma być” wpisuje zgłaszający z powodem. Lista „Zmiany zleceń” zbiera wszystkie zgłoszenia.',
        },
        { kind: 'image', image: { guide: 'orderChangeRegister', step: 3 }, caption: 'Lista „Zmiany zleceń” — decyzje i oznaczenie „Wprowadzone w TMS”.' },
        {
          kind: 'task',
          title: 'Rozpatrzyć zmianę zlecenia',
          steps: [
            'Otwórz „Zmiany zleceń” w menu i wybierz w polu „Stan” — „Czekają na decyzję”.',
            'Przy zmianie porównaj „było” z „ma być” i przeczytaj powód zgłoszenia.',
            'Kliknij „Akceptuj” albo „Odrzuć” i wpisz powód decyzji.',
            'Po akceptacji wprowadź zmianę ręcznie w TMS.',
            'Kliknij „Wprowadzone w TMS” i „Oznacz jako wprowadzone” — to Twoje oświadczenie, że zmiana jest w TMS.',
          ],
          outcome: 'Import z TMS zamknie zmianę, gdy pokaże wartość „ma być”. Gdy pokaże inną — zmiana trafi do „TMS pokazuje inną wartość (przypomnienie)”.',
        },
        {
          kind: 'terms',
          title: 'Stany zmiany',
          items: [
            { term: 'czeka na decyzję', meaning: 'Zgłoszona, bez zgody.' },
            { term: 'zaakceptowana — do wprowadzenia w TMS', meaning: 'Zgoda jest, trzeba wpisać zmianę w TMS.' },
            { term: 'wprowadzona w TMS — czeka na import', meaning: 'Oznaczona jako wpisana, czeka na potwierdzenie z importu.' },
            { term: 'zamknięta — TMS zgodny', meaning: 'Import pokazał wartość „ma być”.' },
            { term: 'odrzucona / wycofana', meaning: 'Z powodem widocznym przy zmianie i w historii zlecenia.' },
            {
              term: 'akceptuje: …',
              meaning: 'Właściciel zmienianego pola: termin, miejsce i uwagi — CS, waga, LDM i stawka — Administracja. Przycisk „Akceptuj” ma tylko właściciel; osoba realizująca może „Dodaj uwagę”, ale nie decyduje.',
              when: { on: ['orderChangeFieldOwner'] },
            },
            {
              term: 'Zmiana po wyjeździe na załadunek',
              meaning: 'Gdy auto wyruszyło już na załadunek, zmianę zatwierdza dział, do którego należy pole — w Administracji każdy z działu, w Trade Team Leader Trade, we flocie planista główny. Informacja przy zmianie mówi, na kogo czeka.',
              when: { on: ['orderChangeLeadAfterDeparture'] },
            },
          ],
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Aplikacja do TMS nic nie zapisuje — zmianę wpisuje człowiek, a import ją zamyka. Nic nie dzieje się samo.',
        },
      ],
    },
    {
      id: 'korekty-paliwowe',
      title: 'Korekty paliwowe',
      menu: ['fuel-surcharges'],
      when: { on: ['fuelSurcharges'] },
      blocks: [
        {
          kind: 'text',
          text: 'Kartoteka korekt paliwowych ma zakładki „Rodzaje korekt”, „Wartości”, „Przypisania kontrahentów”, „Plik od CS” i „Historia zmian”. Kontrahent ma jedną korektę naraz. Na zleceniu korekta jest tylko informacją w sekcji „Cena i finanse” — nie zmienia marży ani kosztu.',
        },
        { kind: 'image', image: { guide: 'fuelSurcharges', step: 1 }, caption: 'Korekty paliwowe — rodzaje korekt.' },
        {
          kind: 'task',
          title: 'Ustawić korektę paliwową',
          steps: [
            'Otwórz „Korekty paliwowe” w menu i wybierz zakładkę „Wartości”.',
            'Wybierz „Rodzaj korekty” i kliknij „Dodaj wartość” — wpisz wartość (procent albo kwotę), okres od–do, źródło i powód.',
            'Otwórz kartę kontrahenta i kliknij „Przypisz korektę” — wybierz rodzaj, datę i wpisz powód.',
            'Sprawdź na zleceniu tego kontrahenta, w sekcji „Cena i finanse”, że korekta pokazuje się na dzień załadunku albo rozładunku.',
          ],
          outcome: 'Nowe przypisanie kończy poprzednie tylko za zgodą. Każda zmiana trafia do historii.',
        },
        {
          kind: 'task',
          title: 'Wczytać plik „klient → korekta” od CS',
          steps: [
            'Wybierz zakładkę „Plik od CS”, kliknij „Wybierz plik” i wskaż plik (CSV albo Excel).',
            'Kliknij „Podgląd” i przeczytaj raport nieznanych kontrahentów i rodzajów.',
            'Po sprawdzeniu podglądu wpisz „Powód” i kliknij „Zapisz przypisania (n)” — zapisują się tylko „nowe przypisanie” i „zmiana korekty”.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Brak wartości to brak, nie zero — dopóki CS nie poda wartości, zlecenie pokazuje „brak — czeka na wartości od CS”.',
        },
      ],
    },
    {
      id: 'rozliczenie',
      title: 'Rozliczenie zlecenia',
      menu: ['settlements'],
      when: { on: ['orderSettlement'] },
      blocks: [
        {
          kind: 'text',
          text: 'Ekran „Rozliczenie” ma dwie listy: „Do rozliczenia” i „Przekazane do fakturowania”. Przy zleceniu widać fracht, postój przy punktach wyliczony z czasu na miejscu według śladu GPS (szacunek ze źródłem), korektę paliwową i awizacje jako informację.',
        },
        { kind: 'image', image: { guide: 'orderSettlement', step: 2 }, caption: 'Postój przy punktach — szacunek ze śladu GPS.' },
        {
          kind: 'task',
          title: 'Rozliczyć zlecenie',
          steps: [
            'Otwórz „Rozliczenie” w menu (albo kafelek „Do rozliczenia” na pulpicie) i wybierz zlecenie z listy „Do rozliczenia”.',
            'Sprawdź „Postój przy punktach — szacunek ze śladu GPS”: kiedy auto stało, ile to trwało i ile ponad czas bezpłatny.',
            'Kliknij „Nalicz postój” albo „Nie naliczaj” — w tym drugim przypadku wpisz powód.',
            'W razie potrzeby kliknij „Dodaj korektę” — kwota (ujemna = obniżka), waluta i powód.',
            'Kliknij „Przekaż do fakturowania” i dopisz notatkę, jeśli trzeba.',
          ],
          outcome: '„Przekaż do fakturowania” jest nieaktywne, dopóki postój nie jest rozstrzygnięty. Fakturę wystawia się poza aplikacją; przekazanego rozliczenia nie da się już zmienić.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Punkt bez pomiaru ma powód (np. auto bez telematyki) — brak to brak, nie zero. Stawka postoju i czas bezpłatny to parametry z historią; rozliczenie z rozstrzygniętym postojem zostaje przy kwocie z chwili decyzji.',
        },
      ],
    },
    {
      id: 'pulpit-administracji',
      title: 'Pulpit przyjęcia zleceń — kolejki Administracji',
      when: { on: ['intakeDepartments'] },
      blocks: [
        {
          kind: 'terms',
          title: 'Kafelki Administracji',
          items: [
            { term: 'Do kontroli (Administracja)', meaning: 'Prezlecenia do sprawdzenia przed zapisem do TMS.' },
            { term: 'Zatwierdzone, przed plikiem', meaning: 'Treść zatwierdzona, plik jeszcze nie powstał.' },
            { term: 'Błąd zapisu do VIA', meaning: 'Plik nie dotarł — trzeba sprawdzić przyczynę i ponowić ręcznie.' },
            { term: 'Błąd zapisu — ponawiane automatycznie', meaning: 'Aplikacja jeszcze ponawia dostarczenie.', when: { on: ['viaDeliveryAutoRetry'] } },
            { term: 'Plik wytworzony, czeka na TMS', meaning: 'Plik wyszedł, czekamy na import z TMS.' },
          ],
        },
        {
          kind: 'text',
          when: { on: ['adminDashboardSections'] },
          text: 'Pod kafelkami stoją dwie sekcje. „Ostatnie zapisy do TMS” — dziesięć ostatnich zleceń z plikiem: numer w TMS (gdy import go przyniósł), stan zapisu, liczba prób i ostatni błąd. „Sprzedane przez Trade” — zlecenia z ostatnich 30 dni z przewoźnikiem, ceną klienta, kosztem przewoźnika i marżą z pasmem. Bez kosztu przewoźnika marża jest pusta z powodem, nie zero.',
        },
        {
          kind: 'image',
          image: { guide: 'adminDashboardSections', step: 1 },
          caption: 'Ostatnie zapisy do TMS na pulpicie Administracji.',
          when: { on: ['adminDashboardSections'] },
        },
        {
          kind: 'task',
          title: 'Rozpocząć dzień od kolejek',
          steps: [
            'Otwórz „Pulpit przyjęcia zleceń”.',
            'Najpierw kliknij kafelek „Błąd zapisu do VIA” i rozwiąż błędy — zlecenia czekają na plik.',
            'Potem „Do kontroli (Administracja)” — sprawdź i zapisz kolejne zlecenia.',
            'Na koniec przejrzyj „Plik wytworzony, czeka na TMS” — co długo czeka, sprawdź w „Zleceniach VIA”.',
          ],
        },
      ],
    },
    {
      id: 'regula-podwykonawcy',
      title: 'Reguła podwykonawcy na karcie kontrahenta',
      when: { on: ['subcontractorRuleOwnership'] },
      blocks: [
        {
          kind: 'text',
          text: 'Reguła podwykonawcy klienta (dozwolony, warunkowy, zakaz) decyduje, czy zlecenie można wystawić na giełdę. Ma właściciela i datę, od której obowiązuje; zmianę można zaplanować i odwołać z powodem. Regułę zmieniają CS i administrator, a Administracja widzi ją w sekcji „Podwykonawca — reguła publikacji na giełdach” i w „Historia reguły podwykonawcy”. Szczegóły w rozdziale Customer Service.',
        },
      ],
    },
  ],
};
